/**
 * Odvozene zobrazeni smlouvy pro obrazovky podle klikaciho mockupu
 * `docs/platform/ui/_designs/smlouvy/reos-smlouvy-aplikace-mockup.html`
 * (krok T4).
 *
 * Ciste funkce bez Reactu a bez siti: vstup = DTO z API, vystup = to, co
 * ma editor/list/podpis vyrenderovat. Diky tomu je mockup overitelny
 * testem (`contract-presentation.spec.ts`) bez mockovani fetch.
 *
 * T1/T2/T3 engine zustava nedotknuty — tato vrstva jen SKLADA to, co API
 * uz vrace (viz REPORT-step-T4 ROZHODNUTI T4-4).
 */

import { czechAmountText, formatCzechDate, isCanonicalAmount, t, type MessageKey } from '@reos/i18n';
import type {
  ContractArticleDto,
  ContractCheckDto,
  ContractDetailDto,
  ContractDto,
  ContractTextChangeDto,
  TextChangeKind,
} from './contracts-api.js';
import type { FieldOrigin } from './contracts-api.js';

// ------------------------------------------------------------------ puvod udaje

export type { FieldOrigin };

/** Poradi legendy jako v mockupu; poradi je i poradi barevnych sloupcu. */
export const ORIGIN_LEGEND: readonly FieldOrigin[] = ['kat', 'klient', 'makler', 'pozdeji', 'chybi'];

export const ORIGIN_LABEL_KEY: Readonly<Record<FieldOrigin, MessageKey>> = {
  kat: 'webAdmin.contracts.origin.kat',
  klient: 'webAdmin.contracts.origin.klient',
  'majitel-nabor': 'webAdmin.contracts.origin.majitelNabor',
  makler: 'webAdmin.contracts.origin.makler',
  'makler-nabor': 'webAdmin.contracts.origin.maklerNabor',
  pozdeji: 'webAdmin.contracts.origin.pozdeji',
  chybi: 'webAdmin.contracts.origin.chybi',
};

/**
 * Barevny koš puvodu. Krok P8F4 bod 3: `*-nabor` je ZPRESNENI stitku, ne nova
 * barva — dedi tridu po svem zdroji, aby nahled nemel nestylovane pole.
 * Musi sedet s `ORIGIN_CSS_BUCKET` v `origin.ts` na serveru.
 */
const ORIGIN_CSS_BUCKET: Readonly<Record<FieldOrigin, string>> = {
  kat: 'kat',
  klient: 'klient',
  'majitel-nabor': 'klient',
  makler: 'makler',
  'makler-nabor': 'makler',
  pozdeji: 'pozdeji',
  chybi: 'chybi',
};

/** CSS trida obalu v zivem nahledu (musi sedet s `origin.ts` na serveru). */
export function originCssClass(origin: FieldOrigin): string {
  return `reos-f reos-f--${ORIGIN_CSS_BUCKET[origin]}`;
}

/**
 * Krok P8F4 bod 4 — „Typ předmětu" SE PREDVYPLNI Z DRUHU ZAKAZKY.
 *
 * Zprostredkovatelska smlouva na byt mela „Typ předmětu —" prazdny, prestoze
 * nemovitost zakazky je jednotka (nalez P8-N5); engine z nej sklonuje slovo
 * ve vete, takze bez nej smlouva o predmetu nerika nic. Druh nemovitosti
 * (`PropertyListItem.kind`) uz obrazovka pri zakladani cte.
 *
 * `complex` (soubor nemovitosti) mapovani ZAMERNE NEMA — typ predmetu by si
 * aplikace vymyslela (pravidlo L0), makler ho vybere sam.
 */
const SUBJECT_TYPE_BY_PROPERTY_KIND: Readonly<Record<string, string>> = {
  unit: 'byt',
  building: 'dum',
  land: 'pozemek',
};

export function subjectTypeForPropertyKind(kind: string | null | undefined): string | null {
  if (kind === null || kind === undefined) return null;
  return SUBJECT_TYPE_BY_PROPERTY_KIND[kind] ?? null;
}

// ------------------------------------------------------------------ stav smlouvy

/**
 * Smlouva ma v detailu k dispozici `fieldOrigins` (server, `origin.ts`), takze
 * pocet "chybi" a "pozdeji" se da spocitat BEZ opakovanych dotazu — na rozdil
 * od listu, kde je radek jen `ContractDto`.
 */
// Krok AS3: pocitani zije v `@reos/today-metrics` — tutez funkci vola kontrola
// Asistentky `contract_missing_fields` (smlouva s chybejicim povinnym udajem).
import { contractCounts } from '@reos/today-metrics';

export { contractCounts };

/**
 * Text stavu do hlavicky editoru i do sloupce seznamu ("1 chybí · 3 později").
 *
 * Krok APP3 bod 1 (nalez A31): cislo jde z OSNOVY serveru (`fillPlan`), ne
 * z `fieldOrigins` — jinak rika jinou pravdu nez „zbývá N" u tlacitka
 * k podpisu a nez panel kontrol.
 */
export function statusCountValues(
  detail: Pick<ContractDetailDto, 'fieldOrigins' | 'fillPlan'>,
): { errors: number; later: number } {
  return contractCounts(detail);
}

// ------------------------------------------------------------------ strany

/*
 * Krok SML6 bod 2: pribyl ZAJEMCE. Zprostredkovatelska smlouva ma na jedne
 * strane kancelar (zprostredkovatel — ta neni seznam osob, hodnoty jsou
 * `zprostredkovatel.*` z CRM) a na druhe ZAJEMCE, ktery muze byt vic nez
 * jeden (manzele). Bez teto role spadla smlouva z importovaneho mustru
 * o prodeji do vychozi vetve najmu a editor nabizel „Pronajímatel 1".
 */
export type PartyRole = 'pronajimatel' | 'najemce' | 'prodavajici' | 'kupujici' | 'zajemce';

export const PARTY_ROLE_LABEL_KEY: Readonly<Record<PartyRole, MessageKey>> = {
  pronajimatel: 'webAdmin.contracts.editor.landlord',
  najemce: 'webAdmin.contracts.editor.tenant',
  prodavajici: 'webAdmin.contracts.editor.seller',
  kupujici: 'webAdmin.contracts.editor.buyer',
  zajemce: 'webAdmin.contracts.editor.interested',
};

/** Prefix klice opakovane role ve slovniku, napr. `najemce[].jmeno`. */
export const PARTY_FIELD_PREFIX: Readonly<Record<PartyRole, string>> = {
  pronajimatel: 'pronajimatel[]',
  najemce: 'najemce[]',
  prodavajici: 'prodavajici[]',
  kupujici: 'kupujici[]',
  zajemce: 'zajemce[]',
};

/**
 * Pole strany presne podle mockupu: jmeno, rodne cislo (maskovane), bydliste,
 * e-mail, telefon. `doklad` (cislo dokladu) tu vedome NENI — je to citlive
 * pole pro AML, ktere vyplnuje klient pres magic link (KP1), ne makler;
 * navic melo drive stejny popisek jako rodne cislo, takze editor ukazoval
 * „Rodné číslo" dvakrat (zivy nalez T4).
 */
export const PARTY_FIELD_KEYS = ['jmeno', 'rc', 'adresa', 'email', 'telefon'] as const;
/**
 * Zivy pruchod 13. 10. (V4): spoluvlastnicky podil pouziva jen
 * `repeatItemTemplate` prodavajiciho a kupujiciho (`RT_PRODAVAJICI`,
 * `RT_KUPUJICI` v demo-templates.ts) — pronajimatel/najemce/zajemce ho
 * nemaji. Samostatny klic, ne dalsi polozka `PARTY_FIELD_KEYS`, aby karta
 * „N z 5 polí" u najmu nezacala pocitat se sesti poli, ktera nikdy nejdou
 * vyplnit (viz `partyFieldKeysFor` nize).
 */
export const PARTY_SHARE_FIELD_KEY = 'podil' as const;
export type PartyFieldKey = (typeof PARTY_FIELD_KEYS)[number] | typeof PARTY_SHARE_FIELD_KEY;

export const PARTY_FIELD_LABEL_KEY: Readonly<Record<PartyFieldKey, MessageKey>> = {
  jmeno: 'webAdmin.contracts.editor.fieldJmeno',
  rc: 'webAdmin.contracts.editor.fieldRc',
  adresa: 'webAdmin.contracts.editor.fieldAddress',
  email: 'webAdmin.contracts.editor.fieldEmail',
  telefon: 'webAdmin.contracts.editor.fieldPhone',
  podil: 'webAdmin.contracts.editor.fieldPodil',
};

const PARTY_SHARE_ROLES: readonly PartyRole[] = ['prodavajici', 'kupujici'];

/** Pole strany, ktera ma VYPLNIT panel u teto role (V4: podil jen u prodavajiciho/kupujiciho). */
export function partyFieldKeysFor(role: PartyRole): readonly PartyFieldKey[] {
  return PARTY_SHARE_ROLES.includes(role)
    ? [...PARTY_FIELD_KEYS, PARTY_SHARE_FIELD_KEY]
    : PARTY_FIELD_KEYS;
}

/**
 * Rodne cislo (krok UXD1 bod 1): makler ho ted zadava sam (pole bylo `readOnly`,
 * klient pres magic link neni v pilotu k dispozici). Normalizuje psani/vlozeni
 * na `YYMMDD/XXX(X)` — doplni lomitko po 6 cislicich, prijme vlozeny text se
 * zavorkami/mezerami/lomitkem, zahodi vse ostatni.
 */
export function formatRcInput(raw: string): string {
  const digits = raw.replace(/[^0-9]/g, '').slice(0, 10);
  return digits.length > 6 ? `${digits.slice(0, 6)}/${digits.slice(6)}` : digits;
}

/** Zobrazeni mimo fokus (T1): prvnich 6 cislic + maska zbytku. */
export function maskRc(value: string): string {
  const digits = value.replace(/[^0-9]/g, '');
  if (digits === '') return '';
  return `${digits.slice(0, 6)}/••••`;
}

/**
 * Mekka kontrola formatu pro upozorneni pod polem — NIKDY neblokuje ulozeni.
 * Jen pocet cislic (9-10) a delitelnost 11 u desetimistneho RC; datum/stoleti
 * se neresi (na to je plny overovac `checkBirthNumber` v `@reos/documents`,
 * ktery sem nejde importovat — web-admin je browser bundle a na backendovy
 * kontext nesmi zavislet, viz `docs/architecture` hranice kontextu).
 */
export function rcFormatLooksInvalid(raw: string): boolean {
  const digits = raw.replace(/[^0-9]/g, '');
  if (digits === '') return false;
  if (digits.length < 9 || digits.length > 10) return true;
  if (digits.length === 10 && Number(digits) % 11 !== 0) {
    const legacyOk = Number(digits.slice(0, 9)) % 11 === 10 && digits.slice(9) === '0';
    if (!legacyOk) return true;
  }
  return false;
}

export interface Party {
  readonly role: PartyRole;
  /** 0 = prvni strana role (mockup: "Pronajimatel 1"). */
  readonly index: number;
  readonly values: Readonly<Record<PartyFieldKey, string>>;
  /** Kdo hodnotu zadal — urcuje barvu (legenda mockupu). */
  readonly source: ContractFieldOriginSource;
  /** `true` = klient potvrdil (stav "potvrzeno" v mockupu). */
  readonly confirmed: boolean;
}

export type ContractFieldOriginSource = 'broker' | 'client' | 'reos_data';

function asArray(value: unknown): readonly unknown[] {
  if (Array.isArray(value)) return value;
  if (value === null || value === undefined || value === '') return [];
  return [value];
}

function asRecord(value: unknown): Readonly<Record<string, unknown>> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

/**
 * Strany ze `field_values`.
 *
 * Ulozny tvar je JEDEN seznam objektu pod klicem role (`pronajimatel[]` =
 * `[{ jmeno, adresa, … }, …]`) — presne ten, ktery engine T1 rozbaluje
 * v `{{opakuj:pronajimatel[]}}` pres `repeatItemTemplate`. Slovnik T1 popisuje
 * POLE polozky (`pronajimatel[].jmeno`), ne samostatne seznamy hodnot; T4
 * puvodne zapisovalo sloupce (`pronajimatel[].jmeno = [jmena]`) a strany se
 * proto do textu smlouvy nikdy nedostaly (zivy nalez T4, viz report §Nalezy).
 * Cteni starsiho sloupcoveho tvaru zustava kvuli konceptum zalozenym predtim.
 *
 * ROZHODNUTI T4-5 plati dal: index strany = pozice v seznamu, zadny dalsi
 * stav v databazi; stav potvrzeno/ceka nese `source` hodnoty.
 */
export function partiesOf(
  fieldValues: Readonly<Record<string, { value: unknown; source: ContractFieldOriginSource }>>,
  roles: readonly PartyRole[] = ['pronajimatel', 'najemce'],
): readonly Party[] {
  const out: Party[] = [];
  for (const role of roles) {
    const prefix = PARTY_FIELD_PREFIX[role];
    const items = asArray(fieldValues[prefix]?.value);
    const roleFields = partyFieldKeysFor(role);
    // Mockup ma vzdy "Pronajimatel 1" a "Najemce 1" a nejdou odebrat — i u
    // ciste prazdneho konceptu. Bez toho editor u nove smlouvy nenabizel ani
    // jedno pole strany a odkaz klientovi nemel ke komu poslat (zivy nalez T4).
    const count = Math.max(
      1,
      items.length,
      ...roleFields.map((f) => asArray(fieldValues[`${prefix}.${f}`]?.value).length),
    );
    const source = fieldValues[prefix]?.source ?? fieldValues[`${prefix}.jmeno`]?.source ?? 'broker';
    for (let i = 0; i < count; i += 1) {
      const item = asRecord(items[i]);
      const values = {} as Record<PartyFieldKey, string>;
      for (const f of roleFields) {
        const legacy = asArray(fieldValues[`${prefix}.${f}`]?.value)[i];
        values[f] = String(item[f] ?? legacy ?? '');
      }
      out.push({ role, index: i, values, source, confirmed: source === 'client' });
    }
  }
  return out;
}

/**
 * Lidsky nazev opakovaneho bloku strany. Vzor T2 ma ve sablone
 * `{{opakuj:najemce[]}}`, takze mezi nevyplnenymi klici je i holy
 * `najemce[]` — ten ale NENI ve slovniku T1 (ten zna az `najemce[].jmeno`),
 * takze UI i kontroly pred odeslanim psaly technicky klic. Mockup chce
 * „Nájemce“ (zivy nalez T4).
 */
/**
 * `true` pro klic, ktery patri oddilu „Smluvni strany" — holy blok
 * (`najemce[]`), jeho pod-pole (`najemce[].jmeno`) i HOLY nazev pole
 * polozky (`jmeno`, `adresa`, …). Ten posledni prichazi z
 * `repeatItemTemplate` (`Nájemce {{jmeno}}, bytem {{adresa}}.`) — kdyz
 * strana neni vyplnena, engine ho hlasi jako nevyplnene pole a editor
 * i kontroly psaly holy technicky klic (zivy nalez T4). Slovnik T1 nema
 * zadne pole bez prefixu, takze kolize nehrozi.
 */
export function isPartyFieldKey(key: string): boolean {
  if ((PARTY_FIELD_KEYS as readonly string[]).includes(key)) return true;
  return Object.values(PARTY_FIELD_PREFIX).some((p) => key === p || key.startsWith(`${p}.`));
}

/** Opakovane bloky vzoru, ktere nejsou stranou smlouvy (krok UXP3). */
const NON_PARTY_REPEAT_LABEL_KEY: Readonly<Record<string, MessageKey>> = {
  'nemovitost.parcely[]': 'webAdmin.contracts.editor.repeatParcels',
};

export function repeatBlockLabelKey(key: string): MessageKey | undefined {
  const role = (Object.keys(PARTY_FIELD_PREFIX) as PartyRole[]).find(
    (r) => PARTY_FIELD_PREFIX[r] === key,
  );
  if (role !== undefined) return PARTY_ROLE_LABEL_KEY[role];
  // Krok UXP3 (P1-5): opakovany blok, ktery NENI strana — ve slovniku T1 jsou
  // jen jeho pod-pole (`nemovitost.parcely[].cislo`), takze holy klic se
  // v kontrolach psal technicky („Chybí: nemovitost.parcely[]").
  const repeatKey = NON_PARTY_REPEAT_LABEL_KEY[key];
  if (repeatKey !== undefined) return repeatKey;
  // Holy nazev pole polozky z `repeatItemTemplate` (`jmeno`, `adresa`, …,
  // „V4": i `podil` u prodavajiciho/kupujiciho).
  return ([...PARTY_FIELD_KEYS, PARTY_SHARE_FIELD_KEY] as readonly string[]).includes(key)
    ? PARTY_FIELD_LABEL_KEY[key as PartyFieldKey]
    : undefined;
}

/** Vsechny role, ktere slovnik T1 zna — v poradi, v jakem se vykresluji. */
const ALL_PARTY_ROLES: readonly PartyRole[] = [
  'prodavajici',
  'kupujici',
  'zajemce',
  'pronajimatel',
  'najemce',
];

/**
 * Role stran, ktere SMLOUVA SKUTECNE MA.
 *
 * Krok PIL1 (vada V18 generalky GEN1): u zprostredkovatelske smlouvy
 * o PRODEJI stala v panelu podpisu strana „Nájemce 1" (snimek `GEN1/6-17`).
 * Pricina: role se hadaly z `contractType`, a `zprostredkovatelska` padala do
 * vychozi vetve najmu — prestoze vzor „Zprostředkovatelská smlouva — prodej"
 * ma opakovany blok `{{opakuj:prodavajici[]}}`.
 *
 * Pravdu proto nese SAMA SMLOUVA, ne jeji typ: opakovane bloky v textu
 * clanku (to, co vzor opravdu vyplnuje) a klice uz vyplnenych stran. Typ
 * zustava jen jako zaloha, kdyz volajici smlouvu po ruce nema.
 */
export function partyRolesIn(
  contract: Pick<ContractDto, 'articles' | 'fieldValues'>,
): readonly PartyRole[] {
  const text = contract.articles
    .flatMap((article) => article.blocks.map((block) => `${block.text} ${block.manualOverride ?? ''}`))
    .join('\n');
  const keys = Object.keys(contract.fieldValues);
  return ALL_PARTY_ROLES.filter((role) => {
    const prefix = PARTY_FIELD_PREFIX[role];
    if (text.includes(`{{opakuj:${prefix}}}`)) return true;
    // Krok SML6: sablona z NAHRANEHO MUSTRU nemusi mit opakovany blok —
    // mapovaci obrazovka do ni dosadi rovnou pole strany
    // (`{{zajemce[].jmeno}}`). I to je strana, kterou smlouva ma.
    if (text.includes(`{{${prefix}.`)) return true;
    return keys.some((key) => key === prefix || key.startsWith(`${prefix}.`));
  });
}

/**
 * Role stran smlouvy. Kdyz je po ruce SAMA SMLOUVA, rozhoduje ona
 * (`partyRolesIn`); jinak zbyva odhad z typu — mockup je postaveny na najmu
 * (pronajimatel / najemce), kupni a rezervacni vzory T2 nesou prodavajiciho
 * a kupujiciho.
 */
export function partyRolesFor(
  contractType: string,
  contract?: Pick<ContractDto, 'articles' | 'fieldValues'> | undefined,
): readonly PartyRole[] {
  const fromContract = contract === undefined ? [] : partyRolesIn(contract);
  if (fromContract.length > 0) return fromContract;
  if (contractType === 'kupni' || contractType === 'rezervace') return ['prodavajici', 'kupujici'];
  // SML6 bod 2: zprostredkovani ma zajemce (kancelar neni seznam osob).
  if (contractType === 'zprostredkovatelska') return ['zajemce'];
  return ['pronajimatel', 'najemce'];
}

export type FieldValuesPatch = Record<string, { value: unknown; source: ContractFieldOriginSource }>;

/**
 * Seznam stran role v ulozném tvaru enginu — `[{ jmeno, adresa, … }, …]`.
 * Skladá se ze SOUCASNEHO stavu (vcetne dorovnani starsiho sloupcoveho
 * tvaru), takze zapis nikdy neztrati uz vyplnene pole jine strany.
 */
function partyItems(
  fieldValues: Readonly<Record<string, { value: unknown; source: ContractFieldOriginSource }>>,
  role: PartyRole,
): Record<string, string>[] {
  return partiesOf(fieldValues, [role]).map((p) => ({ ...p.values }));
}

/**
 * `fieldValues` patch pro zmenu JEDNOHO pole JEDNE strany. Zapisuje cely
 * seznam role, protoze engine T1 rozbaluje `{{opakuj:role[]}}` nad seznamem
 * objektu; kratsi seznam se dorovna prazdnymi polozkami, aby indexy stran
 * zustaly stabilni (ROZHODNUTI T4-5).
 */
export function partyFieldPatch(
  fieldValues: Readonly<Record<string, { value: unknown; source: ContractFieldOriginSource }>>,
  role: PartyRole,
  index: number,
  field: PartyFieldKey,
  value: string,
): FieldValuesPatch {
  const items = partyItems(fieldValues, role);
  while (items.length <= index) items.push({});
  items[index] = { ...items[index], [field]: value };
  return { [PARTY_FIELD_PREFIX[role]]: { value: items, source: 'broker' } };
}

/**
 * `fieldValues` patch pro „+ pronajimatel" / „+ najemce" z mockupu: prida
 * prazdnou stranu na konec seznamu role.
 */
export function addPartyPatch(
  fieldValues: Readonly<Record<string, { value: unknown; source: ContractFieldOriginSource }>>,
  role: PartyRole,
): FieldValuesPatch {
  const items = partyItems(fieldValues, role);
  items.push({});
  return { [PARTY_FIELD_PREFIX[role]]: { value: items, source: 'broker' } };
}

/**
 * `fieldValues` patch pro „Odebrat" u strany (krok UXS2 bod 3).
 *
 * R.B. 1. 10.: „+ Pronajímatel" stranu rovnou ulozilo a zpatky uz nevedla
 * zadna cesta — v demo smlouve tak zustal prazdny „Pronajímatel 2". PRVNI
 * strana role se odebrat NESMI (mockup: „nelze odebrat"): smlouva bez strany
 * nema komu poslat odkaz a editor by u ni nenabidl ani jedno pole (nalez T4).
 * Mimo rozsah je patch prazdny, takze obsluha tlacitka nikdy neposle PATCH,
 * ktery by seznam prepsal nazdarbuh.
 */
export function removePartyPatch(
  fieldValues: Readonly<Record<string, { value: unknown; source: ContractFieldOriginSource }>>,
  role: PartyRole,
  index: number,
): FieldValuesPatch | null {
  if (index <= 0) return null;
  const items = partyItems(fieldValues, role);
  if (index >= items.length) return null;
  return {
    [PARTY_FIELD_PREFIX[role]]: {
      value: items.filter((_item, i) => i !== index),
      source: 'broker',
    },
  };
}

/** `null` = prazdne udaje (v mockupu ctverec "čeká na klienta"). */
export function partyDisplayName(party: Party): string | null {
  const name = party.values.jmeno.trim();
  return name === '' ? null : name;
}

/**
 * Jmeno ROLE strany do prehledu (krok UXS1 bod 2): „Pronajímatel", a teprve
 * kdyz je te role ve smlouve vic, „Pronajímatel 2". R.B. 1. 10. videl
 * v prehledu „Pronajímatel 1, Nájemce 1 čeká" — poradi u jedine strany je
 * informace o databazi, ne o smlouve.
 */
export function partyRoleName(
  parties: readonly Party[],
  party: Party,
  locale: 'cs' | 'en' = 'cs',
): string {
  const role = t(PARTY_ROLE_LABEL_KEY[party.role], { locale });
  const sameRole = parties.filter((p) => p.role === party.role).length;
  return sameRole > 1 ? `${role} ${String(party.index + 1)}` : role;
}

/** Totez pro stranu podpisu, u ktere zname jen `partyKey` a index z API. */
export function signerFallbackName(
  partyKey: string,
  partyIndex: number,
  locale: 'cs' | 'en' = 'cs',
): string {
  const key = (PARTY_ROLE_LABEL_KEY as Readonly<Record<string, MessageKey>>)[partyKey];
  // Neznama role (novejsi API, cizi vzor) nesmi obrazovku shodit — radeji
  // strojovy klic nez prazdne jmeno u strany, ktera ma podepsat.
  const role = key === undefined ? partyKey : t(key, { locale });
  return `${role} ${String(partyIndex + 1)}`;
}

// ------------------------------------------------- stav strany (krok SML2 bod 7)

/**
 * Stav odkazu strany podle SKUTECNE pozvanky (`/v1/client-invitations`).
 *
 * Do SML2 editor rozlisoval jen „klient potvrdil" × „nepotvrdil" a stitek
 * tvrdil „odkaz odeslán" i tam, kde se nic neodeslalo (nalez UXS2 §7.2).
 * Pozvanka ale stav nese: `sentAt`, `openedAt`, `submittedAt`, `revokedAt`.
 */
export type PartyLinkState = 'no-invite' | 'sent' | 'filling' | 'done' | 'revoked';

export interface PartyLinkStatus {
  readonly state: PartyLinkState;
  /** Kolik poli strany uz hodnotu ma (predloha: „vyplňuje · 3 z 6"). */
  readonly done: number;
  readonly total: number;
}

export interface PartyInvitationRef {
  readonly partyKey: string;
  readonly partyIndex: number;
  readonly status: string;
  readonly openedAt: string | null;
  readonly submittedAt: string | null;
  readonly revokedAt: string | null;
}

export function partyLinkStatus(
  party: Party,
  invitations: readonly PartyInvitationRef[],
): PartyLinkStatus {
  const fields = partyFieldKeysFor(party.role);
  const total = fields.length;
  const done = fields.filter((f) => party.values[f].trim() !== '').length;
  const invitation = invitations.find(
    (i) => i.partyKey === party.role && i.partyIndex === party.index,
  );
  if (invitation === undefined) {
    // Bez pozvanky muze byt strana presto hotova — makler ji vyplnil sam.
    return { state: done === total ? 'done' : 'no-invite', done, total };
  }
  if (invitation.revokedAt !== null || invitation.status === 'zneplatneno') {
    return { state: 'revoked', done, total };
  }
  if (invitation.submittedAt !== null || invitation.status === 'zkontrolovano-maklerem') {
    return { state: 'done', done, total };
  }
  if (invitation.openedAt !== null || invitation.status === 'rozpracovano') {
    return { state: 'filling', done, total };
  }
  return { state: 'sent', done, total };
}

export const PARTY_LINK_STATE_KEY: Readonly<Record<PartyLinkState, MessageKey>> = {
  'no-invite': 'webAdmin.contracts.editor.partyStateNoInvite',
  sent: 'webAdmin.contracts.editor.partyStateSent',
  filling: 'webAdmin.contracts.editor.partyStateFilling',
  done: 'webAdmin.contracts.editor.partyStateDone',
  revoked: 'webAdmin.contracts.editor.partyStateRevoked',
};

// --------------------------------------- navrh z dat zakazky (krok SML2 bod 5)

/**
 * Fakt nemovitosti, ze ktereho se da NAVRHNOUT hodnota pole smlouvy.
 *
 * Mapa je zamerne MALA a doslovna — jen pary, kde fakt a pole znamenaji
 * TOTEZ. `rooms_count` (pocet pokoju) napriklad neni `nemovitost.dispozice`
 * („3+1"), takze tam navrh nevznika; vymyslet ho by znamenalo nabidnout
 * maklerovi hodnotu, kterou by do smlouvy nemel vzit.
 *
 * Zdroj faktu je inzerat/prověření zakazky (`property_facts`) — proto
 * popisek „Inzerát této zakázky uvádí" a proto je to NAVRH s tlacitkem
 * „Použít", ne tiche doplneni.
 */
export const SUGGESTION_FACT_BY_FIELD: Readonly<Record<string, string>> = {
  'najem.najemne': 'rent_base_amount',
  'obchod.cena': 'asking_price',
  'nemovitost.uzitna-plocha': 'usable_area_m2',
  'nemovitost.patro': 'floor_number',
  'nemovitost.penb-trida': 'energy_class',
};

export interface SuggestionFact {
  readonly factKey: string;
  readonly value: unknown;
}

/**
 * Navrzena hodnota pole z faktu zakazky; `null` kdyz fakt neni (pak se panel
 * vpravo o navrhu vubec nezmini — zadne prazdne „nabídka: —").
 *
 * `Money` fakt je `{ amount, currency }` (CLAUDE.md §3: nikdy `number`);
 * do pole smlouvy jde `amount`, protoze `castka-kc` je cislo v korunach.
 */
export function fieldSuggestion(
  fieldKey: string,
  facts: readonly SuggestionFact[],
): string | null {
  const factKey = SUGGESTION_FACT_BY_FIELD[fieldKey];
  if (factKey === undefined) return null;
  const fact = facts.find((f) => f.factKey === factKey);
  if (fact === undefined) return null;
  const raw = fact.value;
  if (typeof raw === 'object' && raw !== null) {
    const money = raw as { amount?: unknown };
    return typeof money.amount === 'string' ? money.amount : null;
  }
  if (typeof raw === 'number') return String(raw);
  if (typeof raw === 'string' && raw.trim() !== '') return raw;
  return null;
}

// ------------------------------------------- osnova smlouvy (krok SML2 bod 2)

export interface FillPlanLike {
  readonly articles: readonly {
    readonly id: string;
    readonly roman: string;
    readonly missingKeys: readonly string[];
  }[];
}

/**
 * Poradi chybejicich poli pro „Předchozí / Další chybějící" — poradi CLANKU
 * ve smlouve, ne abeceda klicu. Clovek jde dokumentem shora dolu.
 */
export function missingFieldOrder(plan: FillPlanLike): readonly string[] {
  const out: string[] = [];
  for (const article of plan.articles) {
    for (const key of article.missingKeys) if (!out.includes(key)) out.push(key);
  }
  return out;
}

/**
 * Cisla clanku, ve kterych se pole SKUTECNE pouziva (predloha: „Použito na
 * 2 místech: čl. IV/1 a předávací protokol").
 *
 * Zdroj pravdy je samotna sablona — tentyz test jako `groupFieldsByArticle`
 * (token v textu clanku), aby „kde se to pouzije" a „do ktereho oddilu pole
 * patri" nemohly rict neco jineho.
 */
export function fieldUsageRomans(
  articles: readonly { readonly id: string; readonly blocks: readonly { readonly text: string }[] }[],
  plan: { readonly articles: readonly { readonly id: string; readonly roman: string }[] },
  fieldKey: string,
): readonly string[] {
  const romanById = new Map(plan.articles.map((a) => [a.id, a.roman] as const));
  const role = /^(.+?\[\])/.exec(fieldKey)?.[1] ?? fieldKey;
  const out: string[] = [];
  for (const article of articles) {
    const text = article.blocks.map((b) => b.text).join('\n');
    const used =
      text.includes(`{{${fieldKey}}}`) ||
      text.includes(`{{opakuj:${role}}}`) ||
      text.includes(`{{slovy:${fieldKey}}}`) ||
      // `najem.predmet-typ` stoji v textu jako sklonovany predmet najmu.
      (fieldKey === 'najem.predmet-typ' && /\{\{predmet:[1-7]\}\}/.test(text));
    if (!used) continue;
    const roman = romanById.get(article.id) ?? '';
    if (roman !== '' && !out.includes(roman)) out.push(roman);
  }
  return out;
}

/** Pole za/pred danym ve poradi chybejicich; `null` kdyz neni kam jit. */
export function stepMissingField(
  order: readonly string[],
  current: string | null,
  direction: 1 | -1,
): string | null {
  if (order.length === 0) return null;
  const at = current === null ? -1 : order.indexOf(current);
  if (at === -1) return direction === 1 ? (order[0] ?? null) : (order[order.length - 1] ?? null);
  const next = at + direction;
  if (next < 0 || next >= order.length) return null;
  return order[next] ?? null;
}

// --------------------------------------------------------------- kontroly

export type CheckLineKind = 'chybi' | 'pozdeji' | 'lv-ok' | 'lv-warning' | 'jine';

export interface CheckLine {
  readonly kind: CheckLineKind;
  /** Kluc i18n, kterym se radek vypise; `message` je fallback pro cizi kody. */
  readonly key: MessageKey | null;
  readonly message: string;
  /**
   * Pole formulare, na ktere radek ukazuje — klik na polozku kontrol ho
   * zameri (krok UXS2 bod 2). `null` = kontrola bez konkretniho pole
   * (shoda s LV, chybejici priloha).
   */
  readonly fieldKey: string | null;
}

/** Kody, ktere umi zobrazit lidsky (bez technickeho klice pole v textu). */
const LV_MISMATCH_CODE = 'jmeno-neni-vlastnik-lv';

const LABELLED_CHECK_KEY: Readonly<Record<string, MessageKey>> = {
  'jmeno-neni-vlastnik-lv': 'webAdmin.contracts.editor.checkLvWarning',
};

/**
 * "Kontroly pred odeslanim" v lidske podobe (bod 14 auditu): misto
 * `Chybí povinné pole: kancelar.nazev` vraci `Chybí: Název kanceláře`.
 * Chybejici a odsunuta pole bere ze `fillPlan` (jediny vypocet na serveru,
 * krok APP3 bod 1); z kontrol zbyvaji PRAVIDLA (shoda s LV, prilohy, data).
 */
export function checkLines(
  detail: Pick<ContractDetailDto, 'checks'> & Partial<Pick<ContractDetailDto, 'fillPlan'>>,
  labelOf: (fieldKey: string) => string,
): readonly CheckLine[] {
  const out: CheckLine[] = [];
  /*
   * Krok APP3 bod 1 (nalez A31) — PANEL KONTROL NEPOCITA DRUHE CISLO.
   *
   * Do APP3 si tenhle vypocet skladal vlastni mnozinu: `fieldOrigins`
   * (pole, ktera vzor TISKNE) plus kontroly `chybi-povinne` (povinna pole
   * slovniku, i ta mimo text — krok EDIT2 bod 3). Osnova vlevo mela prvni
   * cast, brana podpisu tretí variantu, a demo smlouva Sojovice proto
   * ukazovala „K podpisu zbývá 9" vedle „10 chybí" (audit 1. 11. 2026).
   *
   * Radky ted vznikaji ze seznamu, ktery spocital SERVER (`fillPlan`), takze
   * `checksSummary` nemuze dat jine cislo nez „Hotovo X z Y" a „zbývá N".
   * Poradi je poradi dokumentu, ne poradi klicu v objektu.
   */
  /*
   * Zaloha pro STARSI ODPOVED SERVERU (detail z cache, API pozadu za
   * adminem): prazdny seznam. Tentyz duvod jako `fillPlan ?? EMPTY_FILL_PLAN`
   * v editoru — chybejici nove pole nesmi shodit celou obrazovku.
   */
  for (const fieldKey of detail.fillPlan?.missingKeys ?? []) {
    out.push({
      kind: 'chybi',
      key: 'webAdmin.contracts.editor.checkMissingPrefix',
      message: labelOf(fieldKey),
      // Pole POLOZKY (`nemovitost.parcely[].cislo`) vlastni vstup nema —
      // zadava se ve svem bloku, takze „Přejít" vede na blok.
      fieldKey: selectableFieldOf(
        fieldKey.includes('[].') ? fieldKey.replace(/\[\]\..*$/, '[]') : fieldKey,
      ),
    });
  }
  for (const fieldKey of detail.fillPlan?.laterKeys ?? []) {
    out.push({
      kind: 'pozdeji',
      key: 'webAdmin.contracts.editor.checkLaterPrefix',
      message: labelOf(fieldKey),
      fieldKey: selectableFieldOf(
        fieldKey.includes('[].') ? fieldKey.replace(/\[\]\..*$/, '[]') : fieldKey,
      ),
    });
  }
  const seen = new Set<string>();
  for (const check of detail.checks) {
    // Chybejici pole uz je vypsane z `fillPlan` vys — tady by vzniklo podruhe.
    if (check.code === 'chybi-povinne') continue;
    if (seen.has(check.code)) continue;
    if (check.code === LV_MISMATCH_CODE) {
      out.push({
        kind: 'lv-warning',
        key: LABELLED_CHECK_KEY[check.code] ?? null,
        message: check.message,
        fieldKey: null,
      });
      continue;
    }
    out.push({
      kind: 'jine',
      key: LABELLED_CHECK_KEY[check.code] ?? null,
      message: check.message,
      fieldKey: null,
    });
  }
  return out;
}

/**
 * Pole, ktere se ma SKUTECNE vybrat. Seznam role (`pronajimatel[]`) nema ve
 * formulari vlastni vstup — vybere se JMENO prvni strany te role, protoze
 * presne tim strana zacina.
 *
 * Krok SML2: pouziva to radek kontrol, klik na clanek v osnove i „Další
 * chybějící" — jedna cesta. Bez ni by panel vpravo nabidl textovy vstup pro
 * HOLY klic role a zapis by seznam stran prepsal retezcem.
 */
export function selectableFieldOf(fieldKey: string): string {
  const isPartyList = Object.values(PARTY_FIELD_PREFIX).includes(fieldKey);
  return isPartyList ? `${fieldKey}.jmeno` : fieldKey;
}

/**
 * Souhrn panelu „Kontroly před odesláním" JEDNIM RADKEM (krok UXS2 bod 2).
 *
 * R.B. 1. 10.: 25 radku „Chybí: …" zabralo celou vysku panelu a dokument A4
 * zacinal az na ~960 px, tedy pod okrajem obrazovky. Souhrn drzi panel kratky;
 * polozky se rozbaluji az na vyzadani.
 */
export function checksSummary(lines: readonly CheckLine[]): {
  readonly missing: number;
  readonly later: number;
  readonly other: number;
} {
  let missing = 0;
  let later = 0;
  let other = 0;
  for (const line of lines) {
    if (line.kind === 'chybi') missing += 1;
    else if (line.kind === 'pozdeji') later += 1;
    else other += 1;
  }
  return { missing, later, other };
}

/** Kolik polozek kontrol je videt, nez clovek klikne na „Zobrazit vše". */
export const CHECKS_PREVIEW_LIMIT = 5;

// --------------------------------------------------------------- popisky poli

/**
 * Povaha ovladaciho prvku pole podle slovniku T1 (krok UXS2 bod 4).
 *
 * Rozhoduje, jak se popisek sváže se vstupem: JEDEN vstup unese `<label for>`,
 * skupina voleb (prepinac, zaskrtavatka, seznam polozek) ne — ta se popisuje
 * `aria-labelledby`. Bez teto vazby nenasel `get_by_label('Jméno a příjmení')`
 * nic (zivy nalez R.B. 1. 10.) a popisek nesel pouzit ani ctecim zarizenim.
 */
export type FieldControlKind = 'input' | 'radios' | 'checks' | 'list';

export function fieldControlKind(
  def:
    | {
        readonly type?: string | undefined;
        readonly options?: readonly string[] | undefined;
      }
    | undefined,
): FieldControlKind {
  const options = def?.options ?? [];
  if (def?.type === 'volba' && options.length > 0) return 'radios';
  if (def?.type === 'seznam') return options.length > 0 ? 'checks' : 'list';
  return 'input';
}

/** Shoda s LV: kontrola chybi, ale kladna varianta ma vlastni radek (bod 14 auditu). */
export function hasLvMismatch(checks: readonly ContractCheckDto[]): boolean {
  return checks.some((c) => c.code === LV_MISMATCH_CODE);
}

// --------------------------------------------------------------- shrnuti

export interface ClientSummarySection {
  readonly key: 'subject' | 'price' | 'deposit' | 'rules';
  readonly titleKey: MessageKey;
  readonly lines: readonly string[];
}

/**
 * Hodnota pole smlouvy jako text. Exportovane, protoze tentyz prevod
 * potrebuje i zaklad provize na fakture (`invoice-commission-basis.ts`,
 * krok FAK1 bod 2) — dve kopie „co je prazdna hodnota" by se rozesly.
 */
export function fieldText(
  fieldValues: ContractDetailDto['fieldValues'],
  key: string,
): string | null {
  const v = fieldValues[key]?.value;
  if (v === null || v === undefined || v === '') return null;
  return String(v);
}

/**
 * Datum do shrnuti pro klienta v ceskem tvaru. `field_values` drzi ISO
 * (`2026-10-10`), mockup ukazuje „10. 10. 2026" — klient cte shrnuti, ne
 * databazi. Prevod je `formatCzechDate` z `@reos/i18n` (tentyz, jaky pouziva
 * pole data i dokument); jina hodnota nez ISO datum projde beze zmeny.
 */
function czDate(value: string | null): string | null {
  if (value === null) return null;
  const cz = formatCzechDate(value);
  return cz === '' ? value : cz;
}

/**
 * Krok NOC2 bod 3 (nalez N13): castka v souhrnu pro klienta se sazi TOUZ
 * funkci jako v dokumentu (`czechAmountText` z `@reos/i18n`, SSOT sazby
 * penez). Do NOC2 tu byl vlastni `toLocaleString('cs-CZ').replace(/\s/g, '.')`
 * — klient tedy cetl „15.000,5 Kč", kdezto PDF teze smlouvy „15 000,50 Kč".
 */
function money(value: string | null): string | null {
  if (value === null) return null;
  // „dle dohody" castka neni — jednotka by z nej udelala nesmysl.
  return isCanonicalAmount(value) ? `${czechAmountText(value)} Kč` : value;
}

/**
 * "Co podepisujete" — shrnuti pro klienta, generovane ZE SMLOVY (bod 17
 * auditu), ne psane rucne v UI. Bere primo `field_values`, takze odrazi
 * to, co je opravdu ve smlouve.
 */
export function clientSummary(
  detail: ContractDetailDto,
  subjectLabel: string | null,
): readonly ClientSummarySection[] {
  const v = detail.fieldValues;
  /*
   * Krok UXP4b bod 4 (nalez P2-9) — SHRNUTI MLUVI O TOM, CO SE OPRAVDU
   * PODEPISUJE. Do UXP4b melo shrnuti vzdy nadpis „Předmět a doba nájmu"
   * a datum bralo z poli `najem.*`, takze zprostredkovatelska smlouva NA
   * PRODEJ tvrdila klientovi „Předmět a doba nájmu: byt". Nadpis i doba
   * se proto beru podle TYPU SMLOUVY — tehoz, podle ktereho se uz ridi
   * hlavni podminky niz (`signingConditions`).
   */
  const sale = detail.contractType === 'kupni';
  const reservation = detail.contractType === 'rezervace';
  const brokerage = detail.contractType === 'zprostredkovatelska';
  const months = brokerage ? fieldText(v, 'zprostredkovani.doba-mesice') : null;
  const monthsText =
    months === null || Number.isNaN(Number(months))
      ? null
      : t('webAdmin.contracts.signature.summaryMonths', {
          locale: 'cs',
          values: { count: Number(months) },
        });
  const subjectTitleKey: MessageKey = sale
    ? 'webAdmin.contracts.signature.summarySubjectSale'
    : reservation
      ? 'webAdmin.contracts.signature.summarySubjectReservation'
      : brokerage
        ? 'webAdmin.contracts.signature.summarySubjectBrokerage'
        : 'webAdmin.contracts.signature.summarySubject';
  const subject = [
    subjectLabel,
    // Datum zacatku a konce je pole NAJMU — u prodeje, rezervace ani
    // zprostredkovani zadne takove datum neexistuje a nic se nedomysli.
    ...(sale || reservation || brokerage
      ? [monthsText]
      : [czDate(fieldText(v, 'najem.zacatek')), czDate(fieldText(v, 'najem.konec'))]),
  ]
    .filter((x): x is string => x !== null && x !== '')
    .join(', ');
  const rent = money(fieldText(v, 'najem.najemne'));
  const services = money(fieldText(v, 'najem.zalohy'));
  const deposit = money(fieldText(v, 'najem.kauce'));
  const rules = [fieldText(v, 'najem.max-osob'), fieldText(v, 'najem.zvirata')].filter(
    (x): x is string => x !== null && x !== '',
  );
  const sections: ClientSummarySection[] = [
    {
      key: 'subject',
      titleKey: subjectTitleKey,
      lines: subject === '' ? [] : [subject],
    },
    {
      key: 'price',
      titleKey: 'webAdmin.contracts.signature.summaryPrice',
      lines: [rent, services].filter((x): x is string => x !== null),
    },
    {
      key: 'deposit',
      titleKey: 'webAdmin.contracts.signature.summaryDeposit',
      lines: deposit === null ? [] : [deposit],
    },
    {
      key: 'rules',
      titleKey: 'webAdmin.contracts.signature.summaryRules',
      lines: rules,
    },
  ];
  return sections.filter((s) => s.lines.length > 0);
}

// ------------------------------------------ hlavni podminky (krok SML1 bod 3)

/**
 * Krok NOC4 bod 5 (nalez N24): klic radku souhrnu. Rozsireno o pojmy, ktere
 * patri k JINEMU nez najemnimu vzoru — rezervace a zprostredkovani maji
 * vlastni dobu, kupni smlouva zpusob uhrady a rezervace termin predani.
 */
export type SigningConditionKey =
  | 'rent'
  | 'services'
  | 'price'
  | 'escrow'
  | 'fee'
  | 'reservationTerm'
  | 'handover'
  | 'provize'
  | 'brokerageTerm'
  | 'deposit'
  | 'term'
  | 'changes';

export interface SigningCondition {
  readonly key: SigningConditionKey;
  readonly labelKey: MessageKey;
  /** Hotova hodnota k vypsani; `null` = pole ve smlouve neni vyplnene. */
  readonly value: string | null;
  /** Pocet zmen proti mustru — `changes` nese cislo, ostatni `null`. */
  readonly changeCount?: number;
}

/**
 * HLAVNI PODMINKY na podpisove karte (krok SML1 bod 3, predloha
 * `SmlouvaPodpis.dc.html`: Nájemné · Kauce · Doba nájmu · Změny proti mustru).
 *
 * Tri veci, na kterych tu zalezi:
 *
 * 1. **Hodnoty jsou ZE SMLOUVY**, ne psane v UI. Predloha ma na karte
 *    `[nájemné] Kč / měsíc` jako zastupny text; kdyby to v aplikaci zustalo
 *    cislo napsane vedle formulare, strana by pred podpisem videla neco
 *    jineho, nez co podepisuje.
 * 2. **„Změny proti mustru" je POCET prepsanych bloku** (`manualOverrides`,
 *    krok T3) — jediny zdroj, ktery o rucnich upravach textu vi. „žádné" se
 *    tedy nepise natvrdo: je to prazdna mapa.
 * 3. **Nevyplnena podminka se nevynecha potichu.** Hodnota `null` zustava
 *    v seznamu, aby karta rekla „nájemné: nevyplněno" — strana ma videt, ze
 *    podminka ve smlouve CHYBI, ne ze o ni nikdo nemluvil.
 *
 * Pouziva TYTEZ pomocniky jako `clientSummary` (`czDate`, `money`), takze
 * castka a datum vypadaji na karte i ve shrnuti pro klienta stejne.
 *
 * Krok NOC4 bod 5 (nalez N24) — SOUHRN PATRI TYPU VZORU, NE NAJMU.
 *
 * Zive (5aaea439) ukazovala karta u REZERVACNI smlouvy vedle spravneho
 * „Rezervační poplatek 50 000 Kč" jeste „Kauce nevyplněno" a „Doba nájmu
 * nevyplněno" — dva pojmy z najemni smlouvy, ktere rezervacni vzor vubec
 * nema (neni mezi jeho jedenacti poli). Klient tak pred podpisem cetl dve
 * nesmyslna „nevyplněno" misto skutecne podstatneho udaje (doba rezervace).
 *
 * Radky proto sklada TABULKA podle typu smlouvy a radek se vytiskne jen
 * tehdy, kdyz pole VZOR OPRAVDU MA: `fillPlan.fillerByKey` je seznam vsech
 * poli, ktera sablona pouziva (poslal ho server, `fill-plan.ts`), a hodnota
 * ve smlouve je druhy dukaz, ze pole existuje. Pravidlo 3 vys plati dal: kdyz
 * vzor pole MA a je prazdne, radek zustane a karta rekne „nevyplněno".
 */
interface ConditionSpec {
  readonly key: SigningConditionKey;
  readonly labelKey: MessageKey;
  /** Pole vzoru, ze kterych radek cte; prazdna hodnota = „nevyplněno". */
  readonly fieldKeys: readonly string[];
  readonly value: (v: ContractDetailDto['fieldValues']) => string | null;
}

/** Radky souhrnu podle typu smlouvy — v poradi, v jakem je karta tiskne. */
const CONDITIONS_BY_TYPE: Readonly<Record<string, readonly ConditionSpec[]>> = {
  kupni: [
    {
      key: 'price',
      labelKey: 'webAdmin.contracts.signature.conditionPrice',
      fieldKeys: ['obchod.cena'],
      value: (v) => money(fieldText(v, 'obchod.cena')),
    },
    {
      key: 'escrow',
      labelKey: 'webAdmin.contracts.signature.conditionEscrow',
      fieldKeys: ['obchod.uschova'],
      value: (v) => fieldText(v, 'obchod.uschova') ?? null,
    },
  ],
  rezervace: [
    {
      key: 'fee',
      labelKey: 'webAdmin.contracts.signature.conditionFee',
      fieldKeys: ['obchod.rezervacni-poplatek'],
      value: (v) => money(fieldText(v, 'obchod.rezervacni-poplatek')),
    },
    {
      key: 'reservationTerm',
      labelKey: 'webAdmin.contracts.signature.conditionReservationTerm',
      fieldKeys: ['rezervace.doba-dny', 'rezervace-najem.doba-dny'],
      value: (v) => fieldText(v, 'rezervace.doba-dny') ?? fieldText(v, 'rezervace-najem.doba-dny') ?? null,
    },
    {
      key: 'handover',
      labelKey: 'webAdmin.contracts.signature.conditionHandover',
      fieldKeys: ['termin.predani'],
      value: (v) => czDate(fieldText(v, 'termin.predani')),
    },
  ],
  zprostredkovatelska: [
    {
      key: 'provize',
      labelKey: 'webAdmin.contracts.signature.conditionProvize',
      fieldKeys: ['obchod.provize'],
      value: (v) => money(fieldText(v, 'obchod.provize')),
    },
    {
      key: 'brokerageTerm',
      labelKey: 'webAdmin.contracts.signature.conditionBrokerageTerm',
      fieldKeys: ['obchod.doba-zprostredkovani'],
      value: (v) => fieldText(v, 'obchod.doba-zprostredkovani') ?? null,
    },
  ],
};

/** Najem bytu i nebytu — tytez tri pojmy, jake ma predloha podpisove karty. */
const NAJEM_CONDITIONS: readonly ConditionSpec[] = [
  {
    key: 'rent',
    labelKey: 'webAdmin.contracts.signature.conditionRent',
    fieldKeys: ['najem.najemne'],
    value: (v) => money(fieldText(v, 'najem.najemne')),
  },
  {
    key: 'services',
    labelKey: 'webAdmin.contracts.signature.conditionServices',
    fieldKeys: ['najem.zalohy'],
    value: (v) => money(fieldText(v, 'najem.zalohy')),
  },
  {
    key: 'deposit',
    labelKey: 'webAdmin.contracts.signature.conditionDeposit',
    fieldKeys: ['najem.kauce'],
    value: (v) => money(fieldText(v, 'najem.kauce')),
  },
  {
    key: 'term',
    labelKey: 'webAdmin.contracts.signature.conditionTerm',
    fieldKeys: ['najem.zacatek', 'najem.konec', 'najem.doba'],
    value: (v) => {
      const from = czDate(fieldText(v, 'najem.zacatek'));
      const to = czDate(fieldText(v, 'najem.konec'));
      if (from === null && to === null) return fieldText(v, 'najem.doba') ?? null;
      return [from, to].filter((x): x is string => x !== null).join(' \u2013 ');
    },
  },
];

export function signingConditions(
  detail: Pick<
    ContractDetailDto,
    'fieldValues' | 'manualOverrides' | 'contractType' | 'templateChanges'
  > & { readonly fillPlan?: Pick<ContractDetailDto['fillPlan'], 'fillerByKey'> | undefined },
): readonly SigningCondition[] {
  const v = detail.fieldValues;
  /*
   * Ma vzor tohle pole? `fillerByKey` je vycet poli sablony ze serveru;
   * starsi odpoved ho nese prazdny, a tehdy rozhoduje uz ulozena hodnota —
   * jinak by karta u takove smlouvy neukazala nic.
   */
  const templateKeys = detail.fillPlan?.fillerByKey ?? {};
  const hasField = (key: string): boolean =>
    templateKeys[key] !== undefined || v[key] !== undefined;
  // Krok SML3 bod 2: „Změny proti mustru" pocita SERVER (diff po odstavcich
  // proti mustru, ze ktereho smlouva vznikla) — do SML3 se tu pocitaly
  // prepsane bloky (`manualOverrides`), takze VLOZENA klauzule se na
  // podpisove karte neobjevila a smazany odstavec taky ne. Pocet prepisu
  // zustava jako zaloha pro starsi odpoved serveru, ktera `templateChanges`
  // jeste nenese.
  const changeCount =
    detail.templateChanges?.length ?? Object.keys(detail.manualOverrides).length;
  const specs = CONDITIONS_BY_TYPE[detail.contractType] ?? NAJEM_CONDITIONS;
  const out: SigningCondition[] = specs
    .filter((spec) => spec.fieldKeys.some(hasField))
    .map((spec) => ({ key: spec.key, labelKey: spec.labelKey, value: spec.value(v) }));
  out.push({
    key: 'changes',
    labelKey: 'webAdmin.contracts.signature.conditionChanges',
    value: null,
    changeCount,
  });
  return out;
}

// --------------------------------------------------------------- oddily editoru

export interface EditorFieldGroup {
  /** `null` = pole, na ktere zadny clanek neodkazuje (mimo sablonu). */
  readonly articleId: string | null;
  readonly title: string;
  readonly fields: readonly string[];
}

/**
 * Pole editoru seskupene podle CLanku smlouvy, ve kterem se fakticky
 * pouzivaji (mockup D12: levy panel ma oddily I-VII jako v textu smlouvy).
 *
 * Zdroj pravdy je samotna sablona: token `{{klic}}` v bloku clanku urcuje,
 * kam pole patri. Klic mimo sablonu (vlastni pole kancelare, token v jine
 * sablone) skonci ve skupine `null` na konci — nikdy se neztrati.
 */
export function groupFieldsByArticle(
  articles: readonly { readonly id: string; readonly title: string; readonly blocks: readonly { readonly text: string }[] }[],
  fieldKeys: readonly string[],
): readonly EditorFieldGroup[] {
  const wanted = [...new Set(fieldKeys)];
  const assigned = new Set<string>();
  const groups: EditorFieldGroup[] = [];
  for (const article of articles) {
    const text = article.blocks.map((b) => b.text).join('\n');
    const fields = wanted.filter((key) => {
      if (assigned.has(key)) return false;
      // `{{klic}}` primo, nebo `{{opakuj:role[]}}` — pak se hleda role z
      // prefixu klice (`najemce[].jmeno` -> `najemce[]`).
      const role = /^(.+?\[\])/.exec(key)?.[1] ?? key;
      const used = text.includes(`{{${key}}}`) || text.includes(`{{opakuj:${role}}}`);
      if (!used) return false;
      assigned.add(key);
      return true;
    });
    if (fields.length > 0) groups.push({ articleId: article.id, title: article.title, fields });
  }
  const rest = wanted.filter((key) => !assigned.has(key));
  if (rest.length > 0) {
    groups.push({ articleId: null, title: '', fields: rest });
  }
  return groups;
}

// --------------------------------------------------------------- seznam

export type ListRowAction = 'open' | 'print' | 'signature' | 'pdf' | 'detail' | 'delete';

export const LIST_ROW_ACTION_KEY: Readonly<Record<ListRowAction, MessageKey>> = {
  open: 'webAdmin.contracts.list.actionOpen',
  print: 'webAdmin.contracts.list.actionPrint',
  signature: 'webAdmin.contracts.list.actionSignature',
  pdf: 'webAdmin.contracts.list.actionPdf',
  detail: 'webAdmin.contracts.list.actionDetail',
  delete: 'webAdmin.contracts.list.actionDelete',
};

/**
 * Akce v sloupci akci seznamu. Podle typu dokumentu a stavu: koncept jde
 * otevrit, tisknout a smazat (FIX2 bod 3), odeslane ma "stav podpisu",
 * podepsane ma PDF. Kdyz je radek jineho typu nez smlouva (souhlas, AML),
 * jde o "detail".
 */
export function listRowActions(row: ContractDto): readonly ListRowAction[] {
  if (row.status === 'draft') return ['open', 'print', 'delete'];
  if (row.status === 'ready_to_send' || row.status === 'awaiting_signature') {
    return ['open', 'signature', 'print'];
  }
  if (row.status === 'signed' || row.status === 'archived') return ['detail', 'pdf', 'print'];
  return ['detail'];
}

// ---------------------------------------------------------- sablony kancelare

export interface TemplateChainEntry {
  readonly id: string;
  readonly version: number;
  readonly supersedesId: string | null;
}

/**
 * Novejsi verze sablony, ze ktere koncept vznikl; `null` = koncept uz je na
 * aktualni sablone (krok UXS1 bod 5).
 *
 * K cemu to je: mustr kancelare se premapovaval (ES3) — koncepty zalozene
 * pred premapovanim nesou v textu POKYNY Z WORDU („MŮŽE BÝT VÍCE
 * PRONAJÍMATELŮ") a jedno pevne misto pro stranu. V prehledu to musi byt
 * videt a musi se z toho dat vyskocit, ne to tise vydavat za aktualni smlouvu.
 */
export function supersedingTemplateId(
  templates: readonly TemplateChainEntry[],
  sourceKey: string,
): string | null {
  const bySuperseded = new Map<string, TemplateChainEntry>();
  for (const tpl of templates) {
    if (tpl.supersedesId === null) continue;
    const known = bySuperseded.get(tpl.supersedesId);
    if (known === undefined || known.version < tpl.version) bySuperseded.set(tpl.supersedesId, tpl);
  }
  let current = sourceKey;
  let found: string | null = null;
  // Retez muze byt delsi nez jeden krok (v1 -> v2 -> v3); `seen` hlida cyklus
  // v datech, aby obrazovka nikdy nezatuhla.
  const seen = new Set<string>([sourceKey]);
  for (;;) {
    const next = bySuperseded.get(current);
    if (next === undefined || seen.has(next.id)) return found;
    found = next.id;
    seen.add(next.id);
    current = next.id;
  }
}

/**
 * Tokeny, ktere nejsou pole slovniku (sklonovani predmetu, sluzby maji vlastni
 * sekci).
 *
 * Krok SMLQA1: vyctem se dorovnava na `templateTokenFieldKey` v enginu
 * (`@reos/documents`, fill-plan.ts) — admin na nej nesmi importem dosahnout
 * (hranice kontextu: web-admin nezavisi na documents), takze jsou to dve
 * kopie teze pravdy a MUSI nest tytez rezijni tokeny. Bez `soucet:` ukazovala
 * karta vzoru MV reality o jedno pole vic, nez smlouva ma, a „Upravit
 * mapování" nabizelo prejmenovat token souctu — po prejmenovani by veta
 * „celkem tedy" prestala pocitat.
 */
const NON_FIELD_TOKEN_PREFIXES = [
  'predmet:',
  'soucet:',
  'odkaz:',
  'shoda:',
  'predlozka:',
] as const;
const NON_FIELD_TOKENS = new Set(['sluzby']);

/**
 * Skutecne pouzita pole sablony — skenuje `{{…}}` primo v textu clanku,
 * NE `tpl.mapping` (to je jen audit navrhu z mapovaci obrazovky; po ulozeni
 * muze zustat prazdny, i kdyz text pole ma — krok T6 bod 1: karta sablony
 * ukazovala „0 polí“, přestože smlouva z ní vyrobená chybějící údaje mela).
 */
export function templateFieldKeys(articles: readonly ContractArticleDto[]): readonly string[] {
  const keys = new Set<string>();
  const re = /\{\{([^}]+)\}\}/g;
  for (const article of articles) {
    for (const block of article.blocks) {
      for (const match of block.text.matchAll(re)) {
        const raw = match[1] ?? '';
        const token = raw.startsWith('opakuj:')
          ? raw.slice('opakuj:'.length)
          : raw.startsWith('slovy:')
            ? raw.slice('slovy:'.length)
            : raw;
        if (NON_FIELD_TOKENS.has(token)) continue;
        if (NON_FIELD_TOKEN_PREFIXES.some((p) => token.startsWith(p))) continue;
        keys.add(token);
      }
    }
  }
  return [...keys];
}

/** Pocet poli na karte sablony kancelare (mockup: „31 polí“). */
export function officeTemplateFieldCount(tpl: {
  readonly articles: readonly ContractArticleDto[];
}): number {
  return templateFieldKeys(tpl.articles).length;
}

/**
 * Prejmenuje vsechny vyskyty pole `{{oldKey}}` na `{{newKey}}` napric clanky —
 * zaklad obrazovky „Upravit mapování“ (krok T6 bod 1: karta mela tlacitko
 * Mapování, ktere ale nic neumoznovalo upravit).
 */
export function renameTemplateField(
  articles: readonly ContractArticleDto[],
  oldKey: string,
  newKey: string,
): ContractArticleDto[] {
  const from = `{{${oldKey}}}`;
  const to = `{{${newKey}}}`;
  return articles.map((article) => ({
    ...article,
    blocks: article.blocks.map((block) => ({ ...block, text: block.text.split(from).join(to) })),
  }));
}

/** Odebere pole ze sablony — misto tokenu zustane v textu prazdno. */
export function removeTemplateField(
  articles: readonly ContractArticleDto[],
  key: string,
): ContractArticleDto[] {
  const from = `{{${key}}}`;
  return articles.map((article) => ({
    ...article,
    blocks: article.blocks.map((block) => ({ ...block, text: block.text.split(from).join('') })),
  }));
}

// ------------------------------------------- zmeny proti mustru (krok SML3)

/**
 * Kam patri vlozeni klauzule podle toho, KDE STOJI KURZOR (krok SML3 bod 3):
 * `articleId` clanku, ve kterem kurzor je, a `blockId` odstavce, ZA ktery se
 * vklada. `null` = kurzor nestoji v zadnem odstavci smlouvy, takze „Vložit"
 * nema kam — a rict to je spravnejsi nez vlozit klauzuli na konec dokumentu,
 * kde ji clovek nehleda.
 */
export function articleOfBlock(
  articles: readonly ContractArticleDto[],
  blockId: string | null,
): { readonly articleId: string; readonly blockId: string | null } | null {
  if (blockId === null) return null;
  const article = articles.find((a) => a.blocks.some((b) => b.id === blockId));
  if (article === undefined) return null;
  return { articleId: article.id, blockId };
}

/** i18n klic popisku druhu zmeny — jeden pro seznam i pro podpisovou kartu. */
export const TEXT_CHANGE_KIND_KEY: Readonly<Record<TextChangeKind, MessageKey>> = {
  upraveno: 'webAdmin.contracts.text.changeUpraveno',
  vlozeno: 'webAdmin.contracts.text.changeVlozeno',
  smazano: 'webAdmin.contracts.text.changeSmazano',
};

/**
 * Druhy radek polozky zmeny: KDO a KDY (predloha „Richard · 23:38").
 *
 * Jmeno se bere z adresare kolegu; kdyz ho neznáme (zmena z doby pred SML3
 * nebo kolega uz v kancelari neni), napise se jen cas — vymyslet jmeno
 * k auditni stope nelze.
 */
export function textChangeAuthorLine(
  change: Pick<ContractTextChangeDto, 'at' | 'by'>,
  members: ReadonlyMap<string, string>,
  locale: string,
): string {
  const name = change.by === null ? null : (members.get(change.by) ?? null);
  const time =
    change.at === null
      ? null
      : new Intl.DateTimeFormat(locale, { dateStyle: 'short', timeStyle: 'short' }).format(
          new Date(change.at),
        );
  return [name, time].filter((v): v is string => v !== null && v !== '').join(' · ');
}

/**
 * Veta „co se stalo" u polozky seznamu zmen. U klauzule rekne, odkud prisla
 * (predloha: „vloženo z knihovny klauzulí"); jinak nazev clanku, aby bylo
 * videt, ktere misto smlouvy se zmenilo.
 */
export function textChangeSummary(change: ContractTextChangeDto, locale: string): string {
  if (change.clauseTitle !== null && change.clauseTitle !== '') {
    return t('webAdmin.contracts.text.changeFromLibrary', {
      locale,
      values: { name: change.clauseTitle },
    });
  }
  return t(TEXT_CHANGE_KIND_KEY[change.kind], { locale });
}

// ------------------------------------------- opakovane bloky, ktere nejsou strana

/**
 * Krok SMLQA1 — OPAKOVANY BLOK BEZ „+ PŘIDAT" SE NEDAL VYPLNIT.
 *
 * Zivy pruchod 29. 10. 2026 (kopie zprostredkovatelskeho vzoru pilotni
 * kancelare v KR1): makléřka vyplnila VSECHNA pole, ktera editor nabidl,
 * a „Odeslat k podpisu" zustalo zhasnute s hlaskou „1 zástupné místo
 * v textu". Pricina: `nemovitost.parcely[]` je OPAKOVANY BLOK (vzor ho sazi
 * `{{opakuj:nemovitost.parcely[]}}` a cte z nej `cislo`, `vymera`, `druh`),
 * ale editor pro nej nemel vlastni ovladac — radek vznikl z `unfilled` jako
 * OBYCEJNY TEXTOVY VSTUP. Co do nej clovek napsal, se ulozilo jako SKALAR,
 * engine v nem polozky nenasel, dokument dal sazel mezeru a brana podpisu
 * drzela. Strany (`najemce[]`) tim netrpely, protoze ty maji vlastni panel.
 *
 * Polozkova pole se berou ze SLOVNIKU (`catalogs.fields` nese
 * `nemovitost.parcely[].cislo` …), ne z `repeatItemTemplate` vzoru — ten
 * detail smlouvy neposila a prejmenovat kontrakt API kvuli tomu nebylo
 * potreba.
 */
export function repeatItemFieldKeys(
  fields: readonly { readonly key: string }[] | undefined,
  listKey: string,
): readonly string[] {
  const prefix = `${listKey}.`;
  const out: string[] = [];
  for (const f of fields ?? []) {
    if (!f.key.startsWith(prefix)) continue;
    const field = f.key.slice(prefix.length);
    if (field === '' || field.includes('.') || out.includes(field)) continue;
    out.push(field);
  }
  return out;
}

/**
 * Opakovane bloky smlouvy, ktere NEJSOU strana a maji vlastni ovladac.
 * Skladaji se z klicu, ktere smlouva uz zna (`unfilled` + `fieldValues`) —
 * tedy ze seznamu, nad kterym stoji i radky formulare.
 */
export function nonPartyRepeatBlockKeys(
  detail: {
    readonly unfilled: readonly string[];
    readonly fieldValues: Readonly<Record<string, unknown>>;
  },
  fields: readonly { readonly key: string }[] | undefined,
): readonly string[] {
  const kandidati = [...new Set([...detail.unfilled, ...Object.keys(detail.fieldValues)])];
  return kandidati.filter(
    (key) =>
      key.endsWith('[]') &&
      !isPartyFieldKey(key) &&
      repeatItemFieldKeys(fields, key).length > 0,
  );
}

/** Polozky opakovaneho bloku v ulozném tvaru enginu (`[{ cislo, … }, …]`). */
export function repeatItemsOf(
  fieldValues: Readonly<Record<string, { value: unknown; source: ContractFieldOriginSource }>>,
  listKey: string,
): readonly Readonly<Record<string, string>>[] {
  const raw = fieldValues[listKey]?.value;
  if (!Array.isArray(raw)) return [];
  const out: Record<string, string>[] = [];
  for (const item of raw) {
    if (item === null || typeof item !== 'object' || Array.isArray(item)) continue;
    const row: Record<string, string> = {};
    for (const [k, v] of Object.entries(item as Record<string, unknown>)) {
      row[k] = v === null || v === undefined ? '' : String(v);
    }
    out.push(row);
  }
  return out;
}

/**
 * Pocet radku, ktere ovladac vykresli: aspon jeden, aby bylo kam psat.
 * Skalar ulozeny PRED touto opravou (text v `nemovitost.parcely[]`) se
 * zahodit nesmi potichu — `repeatItemsOf` ho nevrati jako radek, takze
 * ovladac zacne prazdnym radkem a stary text zmizi az prvnim zapisem.
 */
export function repeatRowCount(
  fieldValues: Readonly<Record<string, { value: unknown; source: ContractFieldOriginSource }>>,
  listKey: string,
): number {
  return Math.max(1, repeatItemsOf(fieldValues, listKey).length);
}

/** Patch pro zmenu JEDNOHO pole JEDNE polozky opakovaneho bloku. */
export function repeatItemFieldPatch(
  fieldValues: Readonly<Record<string, { value: unknown; source: ContractFieldOriginSource }>>,
  listKey: string,
  index: number,
  field: string,
  value: string,
): FieldValuesPatch {
  const items = repeatItemsOf(fieldValues, listKey).map((row) => ({ ...row }));
  while (items.length <= index) items.push({});
  items[index] = { ...items[index], [field]: value };
  return { [listKey]: { value: items, source: 'broker' } };
}

/** Patch pro „+ přidat" u opakovaneho bloku. */
export function addRepeatItemPatch(
  fieldValues: Readonly<Record<string, { value: unknown; source: ContractFieldOriginSource }>>,
  listKey: string,
): FieldValuesPatch {
  const items = repeatItemsOf(fieldValues, listKey).map((row) => ({ ...row }));
  // Prazdny blok ma v ovladaci jeden (jeste neulozeny) radek — „+ přidat" na
  // nem tedy musi vyrobit DVA, jinak tlacitko napoprve nic neudela.
  items.push({});
  if (items.length === 1) items.push({});
  return { [listKey]: { value: items, source: 'broker' } };
}

/** Patch pro „Odebrat" u polozky; prvni radek zustava (nemá kam zmizet). */
export function removeRepeatItemPatch(
  fieldValues: Readonly<Record<string, { value: unknown; source: ContractFieldOriginSource }>>,
  listKey: string,
  index: number,
): FieldValuesPatch | null {
  const items = repeatItemsOf(fieldValues, listKey);
  if (index <= 0 || index >= items.length) return null;
  return {
    [listKey]: { value: items.filter((_row, i) => i !== index), source: 'broker' },
  };
}
